// Pointer + keyboard interaction on the viewport and the SVG overlay.
import {
  addMarker,
  deleteMarker,
  deleteShape,
  getState,
  markerDisplayNums,
  refresh,
  restoreMarker,
  restoreShape,
  setScale,
  touch,
} from "./state";
import { KIND_LABEL } from "./catalog";
import {
  clientToImage,
  centerOn,
  getZoom,
  panState,
  setPan,
  zoomAt,
} from "./view";
import {
  session,
  setMode,
  setBeforeModeLeave,
  setHover,
  select,
  deselect,
  pushDraftPoint,
  pushMeasurePoint,
  pushCalibratePoint,
  onSession,
} from "./session";
import { finishShapeDraft, commitTensatorChain } from "./shapes";
import { KIND_MIN_POINTS } from "./catalog";

/** Screen-pixel radius within which a draw click snaps to the first vertex. */
const SNAP_PX = 14;
/** Screen-pixel radius within which a tensator click snaps onto an existing post. */
const TENSATOR_SNAP_PX = 22;
/** Minimum posts before a tensator chain can be closed into a loop. */
const TENSATOR_CLOSE_MIN = 3;

function nearFirstDraftPoint(p: { x: number; y: number }): boolean {
  const draft = session().shapeDraft;
  const first = draft[0];
  if (!first) return false;
  return Math.hypot(p.x - first.x, p.y - first.y) * getZoom() <= SNAP_PX;
}

/**
 * Snaps a draw click onto the nearest post of an already-placed tensator
 * chain when close enough, so new chains connect cleanly onto existing ones
 * instead of leaving a small gap.
 */
function snapToExistingTensatorPoint(p: { x: number; y: number }): { x: number; y: number } {
  let best: { x: number; y: number } | null = null;
  let bestDist = Infinity;
  for (const z of getState().shapes) {
    if (z.kind !== "line") continue;
    for (const pt of z.points) {
      const d = Math.hypot(p.x - pt.x, p.y - pt.y) * getZoom();
      if (d <= TENSATOR_SNAP_PX && d < bestDist) {
        bestDist = d;
        best = pt;
      }
    }
  }
  return best ?? p;
}
import { renderOverlay } from "./overlay";
import { toast } from "./ui/toast";
import type { Marker, Shape } from "./types";

const DRAG_THRESHOLD = 4;
let viewport: HTMLElement;
let overlay: SVGSVGElement;

export function initInteraction(vp: HTMLElement, ov: SVGSVGElement): void {
  viewport = vp;
  overlay = ov;

  viewport.addEventListener("pointerdown", onViewportPointerDown);
  viewport.addEventListener("pointermove", onViewportPointerMove);
  viewport.addEventListener("pointerleave", () => setHover(null));
  viewport.addEventListener("dblclick", () => {
    const m = session().mode;
    if (m === "shape" || m === "tensator") finishShapeDraft();
  });

  // Commit any in-progress tensator chain before the tool actually switches.
  setBeforeModeLeave((from) => {
    if (from === "tensator") commitTensatorChain();
  });
  viewport.addEventListener(
    "wheel",
    (ev) => {
      ev.preventDefault();
      zoomAt(ev.clientX, ev.clientY, Math.pow(1.0016, -ev.deltaY));
    },
    { passive: false }
  );
  document.addEventListener("keydown", onKeyDown);

  // Keep the calibration prompt in sync with session state.
  onSession(syncCalibPanel);
}

/* ----------------------------- focus helpers ----------------------------- */

export function centerOnMarker(m: Marker): void {
  centerOn(m.x, m.y);
}
export function centerOnShape(z: Shape): void {
  let cx = 0;
  let cy = 0;
  for (const p of z.points) {
    cx += p.x;
    cy += p.y;
  }
  const n = z.points.length || 1;
  centerOn(cx / n, cy / n);
}
export function focusItem(type: "marker" | "shape", id: string): void {
  select({ type, id });
  const row = document.querySelector(`[data-row="${type}-${id}"]`);
  if (row) row.scrollIntoView({ block: "nearest" });
}

/* ----------------------------- viewport ----------------------------- */

function onViewportPointerDown(ev: PointerEvent): void {
  if (ev.button !== 0 && ev.pointerType === "mouse") return;
  if ((ev.target as Element)?.closest(".toast-wrap, .calib-panel, .dock-wrap")) return;

  const p = clientToImage(ev.clientX, ev.clientY);
  const s = session();

  if (s.mode === "place" && s.placeCat) {
    const m = addMarker(s.placeCat, p.x, p.y);
    focusItem("marker", m.id);
    if (!s.multiPlace) setMode("select");
    return;
  }
  if (s.mode === "shape") {
    // Click near the first vertex (with enough points) closes/snaps the polygon.
    if (s.shapeDraft.length >= KIND_MIN_POINTS[s.shapeKind] && nearFirstDraftPoint(p)) {
      finishShapeDraft();
    } else {
      pushDraftPoint(p);
    }
    return;
  }
  if (s.mode === "tensator") {
    // Click back near the chain's own first post closes it into a loop —
    // same gesture as closing a Fläche.
    if (s.shapeDraft.length >= TENSATOR_CLOSE_MIN && nearFirstDraftPoint(p)) {
      commitTensatorChain(true);
    } else {
      pushDraftPoint(snapToExistingTensatorPoint(p));
    }
    return;
  }
  if (s.mode === "measure") {
    pushMeasurePoint(p);
    return;
  }
  if (s.mode === "calibrate") {
    pushCalibratePoint(p);
    return;
  }

  // select mode: drag background = pan; click without drag = deselect
  const startX = ev.clientX;
  const startY = ev.clientY;
  const { tx: startTx, ty: startTy } = panState();
  let moved = false;
  viewport.setPointerCapture?.(ev.pointerId);
  viewport.classList.add("panning");

  const onMove = (e: PointerEvent) => {
    const dx = e.clientX - startX;
    const dy = e.clientY - startY;
    if (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD) moved = true;
    setPan(startTx + dx, startTy + dy);
  };
  const onUp = () => {
    viewport.removeEventListener("pointermove", onMove);
    viewport.removeEventListener("pointerup", onUp);
    viewport.classList.remove("panning");
    if (!moved) deselect();
  };
  viewport.addEventListener("pointermove", onMove);
  viewport.addEventListener("pointerup", onUp);
}

function onViewportPointerMove(ev: PointerEvent): void {
  const s = session();
  if (s.mode !== "shape" && s.mode !== "tensator") {
    if (s.hoverPoint) setHover(null);
    return;
  }
  const p = clientToImage(ev.clientX, ev.clientY);
  if (s.mode === "tensator") {
    if (s.shapeDraft.length >= TENSATOR_CLOSE_MIN && nearFirstDraftPoint(p)) {
      setHover(s.shapeDraft[0]!, true);
    } else {
      const snapped = snapToExistingTensatorPoint(p);
      setHover(snapped, snapped !== p);
    }
    return;
  }
  const snap =
    s.mode === "shape" &&
    s.shapeDraft.length >= KIND_MIN_POINTS[s.shapeKind] &&
    nearFirstDraftPoint(p);
  setHover(p, snap);
}

function onKeyDown(ev: KeyboardEvent): void {
  const tag = document.activeElement?.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
  const s = session();

  if (ev.key === "Escape") {
    // leaving tensator mode auto-commits the current chain (see setBeforeModeLeave)
    if (s.mode !== "select") setMode("select");
    else deselect();
  } else if (ev.key === "Enter" && (s.mode === "shape" || s.mode === "tensator")) {
    finishShapeDraft();
  } else if ((ev.key === "Delete" || ev.key === "Backspace") && s.selection && !s.readOnly) {
    if (s.selection.type === "marker") removeMarker(s.selection.id);
    else removeShape(s.selection.id);
  }
}

/* ------------------------- overlay item handlers ------------------------- */

export function onMarkerDown(ev: PointerEvent, id: string): void {
  if (session().mode !== "select") return;
  ev.stopPropagation();
  const marker = getState().markers.find((m) => m.id === id);
  if (!marker) return;
  focusItem("marker", id);
  if (marker.locked || session().readOnly) return;

  const startX = ev.clientX;
  const startY = ev.clientY;
  const ox = marker.x;
  const oy = marker.y;
  let moved = false;
  overlay.setPointerCapture?.(ev.pointerId);

  const onMove = (e: PointerEvent) => {
    const dx = (e.clientX - startX) / currentZoom();
    const dy = (e.clientY - startY) / currentZoom();
    if (Math.abs(e.clientX - startX) > DRAG_THRESHOLD || Math.abs(e.clientY - startY) > DRAG_THRESHOLD)
      moved = true;
    marker.x = ox + dx;
    marker.y = oy + dy;
    refresh();
    renderOverlay();
  };
  const onUp = () => {
    overlay.removeEventListener("pointermove", onMove);
    overlay.removeEventListener("pointerup", onUp);
    if (moved) touch();
  };
  overlay.addEventListener("pointermove", onMove);
  overlay.addEventListener("pointerup", onUp);
}

export function onShapeDown(ev: PointerEvent, id: string): void {
  if (session().mode !== "select") return;
  ev.stopPropagation();
  const shape = getState().shapes.find((z) => z.id === id);
  if (!shape) return;
  focusItem("shape", id);
  if (shape.locked || session().readOnly) return;

  const startX = ev.clientX;
  const startY = ev.clientY;
  const orig = shape.points.map((p) => ({ x: p.x, y: p.y }));
  let moved = false;
  overlay.setPointerCapture?.(ev.pointerId);

  const onMove = (e: PointerEvent) => {
    const dx = (e.clientX - startX) / currentZoom();
    const dy = (e.clientY - startY) / currentZoom();
    if (Math.abs(e.clientX - startX) > DRAG_THRESHOLD || Math.abs(e.clientY - startY) > DRAG_THRESHOLD)
      moved = true;
    shape.points = orig.map((p) => ({ x: p.x + dx, y: p.y + dy }));
    refresh();
    renderOverlay();
  };
  const onUp = () => {
    overlay.removeEventListener("pointermove", onMove);
    overlay.removeEventListener("pointerup", onUp);
    if (moved) touch();
  };
  overlay.addEventListener("pointermove", onMove);
  overlay.addEventListener("pointerup", onUp);
}

export function onHandleDown(ev: PointerEvent, id: string, index: number): void {
  if (session().mode !== "select") return;
  ev.stopPropagation();
  const shape = getState().shapes.find((z) => z.id === id);
  if (!shape || shape.locked || session().readOnly) return;
  overlay.setPointerCapture?.(ev.pointerId);
  const startX = ev.clientX;
  const startY = ev.clientY;
  let moved = false;

  const onMove = (e: PointerEvent) => {
    if (Math.abs(e.clientX - startX) > DRAG_THRESHOLD || Math.abs(e.clientY - startY) > DRAG_THRESHOLD)
      moved = true;
    shape.points[index] = clientToImage(e.clientX, e.clientY);
    refresh();
    renderOverlay();
  };
  const onUp = () => {
    overlay.removeEventListener("pointermove", onMove);
    overlay.removeEventListener("pointerup", onUp);
    if (moved) touch();
  };
  overlay.addEventListener("pointermove", onMove);
  overlay.addEventListener("pointerup", onUp);
}

/* --------------------------- delete with undo --------------------------- */

export function removeMarker(id: string): void {
  const displayNum = markerDisplayNums().get(id);
  const removed = deleteMarker(id);
  if (!removed) return;
  if (session().selection?.id === id) deselect();
  toast(`Objekt #${displayNum ?? removed.num} gelöscht`, () => restoreMarker(removed));
}

export function removeShape(id: string): void {
  const removed = deleteShape(id);
  if (!removed) return;
  if (session().selection?.id === id) deselect();
  toast(`${KIND_LABEL[removed.kind]} „${removed.name || "Ohne Namen"}“ gelöscht`, () =>
    restoreShape(removed)
  );
}

/* ---------------------------- calibration ---------------------------- */

function currentZoom(): number {
  return getZoom();
}

function syncCalibPanel(): void {
  const s = session();
  const existing = document.getElementById("calibPanel");
  const shouldShow = s.mode === "calibrate" && s.calibratePts.length === 2;
  if (!shouldShow) {
    existing?.remove();
    return;
  }
  if (existing) return;

  const panel = document.createElement("div");
  panel.className = "calib-panel";
  panel.id = "calibPanel";
  panel.innerHTML = `
    <div class="field" style="min-width:180px;">
      <label>Realer Abstand (Meter)</label>
      <input type="text" id="calibMeters" inputmode="decimal" placeholder="z. B. 4,2" />
    </div>
    <button class="btn primary sm" id="calibApply">Übernehmen</button>
    <button class="btn ghost sm" id="calibCancel">Abbrechen</button>`;
  viewport.appendChild(panel);
  const input = panel.querySelector<HTMLInputElement>("#calibMeters")!;
  input.focus();

  panel.querySelector("#calibApply")!.addEventListener("click", () => {
    const meters = parseFloat(input.value.replace(",", "."));
    if (!meters || meters <= 0) {
      input.focus();
      return;
    }
    const [a, b] = session().calibratePts as [
      { x: number; y: number },
      { x: number; y: number },
    ];
    const pxDist = Math.hypot(b.x - a.x, b.y - a.y);
    setScale(pxDist / meters, true);
    toast(`Maßstab aktualisiert: ${(pxDist / meters).toFixed(2)} px/m`);
    setMode("select");
  });
  panel.querySelector("#calibCancel")!.addEventListener("click", () => setMode("select"));
}
